import { useState } from "react";
import { Phone, Menu, X, ChevronDown, ChevronRight, Shield, MapPin, Building2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import neighborhoodPages from "@/data/neighborhoodPages";

const neighborhoodGroups = [
  {
    label: "Oro Valley",
    icon: Shield,
    color: "text-amber-400",
    items: neighborhoodPages.filter((p) => p.parentArea === "Oro Valley"),
  },
  {
    label: "Tucson Luxury",
    icon: Shield,
    color: "text-amber-400",
    items: neighborhoodPages.filter((p) => p.parentArea === "Tucson" && p.type === "luxury"),
  },
  {
    label: "Marana",
    icon: MapPin,
    color: "text-emerald-400",
    items: neighborhoodPages.filter((p) => p.parentArea === "Marana"),
  },
  {
    label: "Sahuarita",
    icon: MapPin,
    color: "text-emerald-400",
    items: neighborhoodPages.filter((p) => p.parentArea === "Sahuarita"),
  },
  {
    label: "Oracle",
    icon: MapPin,
    color: "text-emerald-400",
    items: neighborhoodPages.filter((p) => p.parentArea === "Oracle"),
  },
  {
    label: "Storm Recovery",
    icon: AlertTriangle,
    color: "text-red-400",
    items: neighborhoodPages.filter((p) => p.type === "storm"),
  },
  {
    label: "Commercial",
    icon: Building2,
    color: "text-blue-400",
    items: neighborhoodPages.filter((p) => p.type === "commercial"),
  },
].filter((g) => g.items.length > 0);

const Header = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileNeighborhoods, setMobileNeighborhoods] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const navLinks = [
    { href: "#services", label: "Services", isAnchor: true },
    { href: "#why-us", label: "Why Choose Us", isAnchor: true },
    { href: "#testimonials", label: "Reviews", isAnchor: true },
    { href: "#faq", label: "FAQ", isAnchor: true },
    { href: "#contact", label: "Contact", isAnchor: true },
  ];

  const serviceAreas = [
    { href: "/tucson-stump-grinding", label: "Tucson" },
    { href: "/oro-valley-stump-grinding", label: "Oro Valley" },
    { href: "/catalina-stump-grinding", label: "Catalina" },
    { href: "/sahuarita-stump-grinding", label: "Sahuarita" },
    { href: "/saddlebrooke-stump-grinding", label: "SaddleBrooke" },
    { href: "/oracle-stump-grinding", label: "Oracle" },
    { href: "/tree-removal", label: "Tree Removal", isSpecial: true },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1A1A1A] border-b-2 border-[#FF4D00]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-10 h-10 bg-[#FF4D00] flex items-center justify-center rounded-sm">
              <Shield className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-bold text-white text-lg uppercase tracking-tight block leading-tight">
                Tucson Stump Grinding
              </span>
              <span className="font-body text-xs text-gray-400 block leading-tight">
                Tree Stump Removal Service
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5" data-testid="desktop-nav">
            {isHomePage ? (
              <a href="#services" className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors">
                Services
              </a>
            ) : (
              <Link to="/#services" className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors">
                Services
              </Link>
            )}

            {/* Service Areas Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors flex items-center gap-1 outline-none" data-testid="service-areas-dropdown">
                Service Areas
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1A1A1A] border-2 border-[#FF4D00]">
                {serviceAreas.map((area) => (
                  <DropdownMenuItem key={area.href} asChild>
                    <Link
                      to={area.href}
                      className={`font-body text-sm ${area.isSpecial ? 'text-green-500 font-semibold' : 'text-gray-300'} hover:text-[#FF4D00] hover:bg-[#2A2A2A] cursor-pointer`}
                    >
                      {area.isSpecial ? `Tree Removal` : `${area.label}, AZ`}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Neighborhoods Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button
                className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors flex items-center gap-1 outline-none"
                onClick={() => setMegaOpen(!megaOpen)}
                data-testid="neighborhoods-dropdown"
              >
                Neighborhoods
                <ChevronDown className={`w-4 h-4 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
              </button>

              {megaOpen && (
                <div className="absolute top-full right-0 mt-2 w-[680px] bg-[#1A1A1A] border-2 border-[#FF4D00] shadow-2xl z-50" data-testid="neighborhoods-mega-menu">
                  <div className="p-4 grid grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto">
                    {neighborhoodGroups.map((group) => (
                      <div key={group.label}>
                        <div className="flex items-center gap-1.5 mb-2 pb-1 border-b border-gray-700">
                          <group.icon className={`w-3.5 h-3.5 ${group.color}`} />
                          <span className={`font-heading text-xs font-bold uppercase tracking-wider ${group.color}`}>
                            {group.label}
                          </span>
                        </div>
                        {group.items.map((page) => (
                          <Link
                            key={page.slug}
                            to={`/${page.slug}`}
                            className="block font-body text-xs text-gray-400 hover:text-[#FF4D00] hover:bg-[#2A2A2A] py-1 px-1.5 transition-colors"
                            onClick={() => setMegaOpen(false)}
                            data-testid={`nav-${page.slug}`}
                          >
                            {page.name}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/blog/stump-grinding-cost-tucson" className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors" data-testid="nav-blog">
              Blog
            </Link>

            {isHomePage ? (
              navLinks.slice(1).map((link) => (
                <a key={link.href} href={link.href} className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors">
                  {link.label}
                </a>
              ))
            ) : (
              <>
                <Link to="/#why-us" className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors">Why Choose Us</Link>
                <Link to="/#testimonials" className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors">Reviews</Link>
                <Link to="/#faq" className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors">FAQ</Link>
                <Link to="/#contact" className="font-body text-sm font-medium text-gray-300 hover:text-[#FF4D00] transition-colors">Contact</Link>
              </>
            )}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+15202007739,2"
              className="flex items-center gap-2 text-white hover:text-[#FF4D00] transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="font-body font-semibold text-sm">(520) 200-7739 Ext 2</span>
            </a>
            <Button asChild className="btn-primary px-6 py-2 text-sm rounded-none" data-testid="header-quote-btn">
              {isHomePage ? (
                <a href="#quote">Free Quote</a>
              ) : (
                <Link to="/#quote">Free Quote</Link>
              )}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => {
              setMobileMenuOpen && setMobileMenuOpen(!mobileMenuOpen);
              setMobileNeighborhoods(false);
              setExpandedGroup(null);
            }}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#1A1A1A] border-t border-gray-800 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-1">
            {isHomePage ? (
              navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block font-body text-base font-medium text-gray-300 hover:text-[#FF4D00] py-2"
                  onClick={() => setMobileMenuOpen && setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))
            ) : (
              <Link to="/" className="block font-body text-base font-medium text-gray-300 hover:text-[#FF4D00] py-2" onClick={() => setMobileMenuOpen && setMobileMenuOpen(false)}>
                Home
              </Link>
            )}

            <Link
              to="/blog/stump-grinding-cost-tucson"
              className="block font-body text-base font-medium text-gray-300 hover:text-[#FF4D00] py-2"
              onClick={() => setMobileMenuOpen && setMobileMenuOpen(false)}
              data-testid="mobile-blog-link"
            >
              Blog
            </Link>

            {/* Service Areas */}
            <div className="border-t border-gray-800 pt-3 mt-3">
              <p className="font-heading text-sm font-bold text-[#FF4D00] uppercase mb-2">Service Areas</p>
              {serviceAreas.map((area) => (
                <Link
                  key={area.href}
                  to={area.href}
                  className={`block font-body text-sm ${area.isSpecial ? 'text-green-500 font-semibold' : 'text-gray-400'} hover:text-[#FF4D00] py-1.5 pl-2`}
                  onClick={() => setMobileMenuOpen && setMobileMenuOpen(false)}
                >
                  {area.isSpecial ? `Tree Removal` : `${area.label}, AZ`}
                </Link>
              ))}
            </div>

            {/* Neighborhoods Accordion */}
            <div className="border-t border-gray-800 pt-3 mt-3">
              <button
                className="flex items-center justify-between w-full py-2"
                onClick={() => setMobileNeighborhoods(!mobileNeighborhoods)}
                data-testid="mobile-neighborhoods-toggle"
              >
                <span className="font-heading text-sm font-bold text-[#FF4D00] uppercase">Neighborhoods</span>
                <ChevronDown className={`w-4 h-4 text-[#FF4D00] transition-transform ${mobileNeighborhoods ? "rotate-180" : ""}`} />
              </button>

              {mobileNeighborhoods && (
                <div className="space-y-1 pb-2">
                  {neighborhoodGroups.map((group) => (
                    <div key={group.label}>
                      <button
                        className="flex items-center justify-between w-full py-1.5 pl-2"
                        onClick={() => setExpandedGroup(expandedGroup === group.label ? null : group.label)}
                        data-testid={`mobile-group-${group.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <span className="flex items-center gap-1.5">
                          <group.icon className={`w-3.5 h-3.5 ${group.color}`} />
                          <span className={`font-body text-sm font-semibold ${group.color}`}>{group.label}</span>
                          <span className="font-body text-xs text-gray-600">({group.items.length})</span>
                        </span>
                        <ChevronRight className={`w-3.5 h-3.5 text-gray-500 transition-transform ${expandedGroup === group.label ? "rotate-90" : ""}`} />
                      </button>

                      {expandedGroup === group.label && (
                        <div className="pl-6 space-y-0.5">
                          {group.items.map((page) => (
                            <Link
                              key={page.slug}
                              to={`/${page.slug}`}
                              className="block font-body text-sm text-gray-400 hover:text-[#FF4D00] py-1"
                              onClick={() => {
                                setMobileMenuOpen && setMobileMenuOpen(false);
                                setMobileNeighborhoods(false);
                                setExpandedGroup(null);
                              }}
                            >
                              {page.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <a
              href="tel:+15202007739,2"
              className="flex items-center gap-2 text-[#FF4D00] py-3 border-t border-gray-800 mt-3"
            >
              <Phone className="w-5 h-5" />
              <span className="font-body font-semibold">(520) 200-7739 Ext 2</span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
